import axios from "axios";
import { handleUnauthorized } from "@/lib/silentSso";
import { showUpgradeNotice } from "@/lib/planGate";
import { enqueueWrite } from "@/lib/offlineQueue";
import { getActiveLearner, isLearnerPath } from "@/lib/learner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Requests go through hikmah-backend's proxy, which only accepts a
// gateway-issued token (minted by the gateway's own /auth/login|register) —
// this app's native tokens are rejected there. The gateway token lives under
// the same localStorage key the old native token used.
const TOKEN_KEY = "hv_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); };
export const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const attachToken = (cfg) => {
  const token = getToken();
  if (token) cfg.headers["Authorization"] = `Bearer ${token}`;
  const learner = getActiveLearner();
  if (learner && isLearnerPath(cfg.url)) cfg.params = { ...(cfg.params || {}), learner_id: learner };
  return cfg;
};
client.interceptors.request.use(attachToken);
client.interceptors.response.use((r) => r, (err) => {
  if (err.response?.status === 402) showUpgradeNotice(typeof err.response.data?.detail === "object" ? err.response.data.detail : {});
  if (err.response?.status === 401) {
    handleUnauthorized({ url: err.config?.url, method: err.config?.method, hadToken: !!getToken(),
      setToken, clearToken: () => setToken(null) });
  }
  return Promise.reject(err);
});

// Not proxied — hikmah-backend's own /auth/* (login/register/force-password-update),
// which is where a token actually gets issued.
export const gatewayClient = axios.create({
  baseURL: process.env.REACT_APP_GATEWAY_URL,
  headers: { "Content-Type": "application/json" },
});
gatewayClient.interceptors.request.use(attachToken);

// Ends the gateway session cookie too (single sign-on) — otherwise the common
// login page would silently sign the user straight back in.
export const gatewayLogout = () =>
  fetch(`${process.env.REACT_APP_GATEWAY_URL}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});

// Sign-out from this app returns to the dashboard's Apps page (same origin as the common login page). The
// gateway session is deliberately kept alive because that page needs it; the dashboard's own "Sign out"
// ends it for every app.
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "http://localhost:3007/login";
export const leaveToDashboard = () => {
  setToken(null);
  window.location.replace(`${new URL(LOGIN_URL).origin}/dashboard`);
};

// Multipart upload helper for the admin media endpoints. The shared `client`
// defaults Content-Type to application/json, which — if left in place —
// stops the browser computing the multipart boundary itself and breaks the
// upload server-side. Setting it to undefined here removes the default so
// the browser sets its own Content-Type (with boundary) for FormData bodies.
export async function uploadFile(url, fieldName, file) {
  const formData = new FormData();
  formData.append(fieldName, file);
  const { data } = await client.post(url, formData, { headers: { "Content-Type": undefined } });
  return data;
}

// Quiz submits are the only progress writes that get queued offline. A quiz
// answered with no connection is saved and replayed later (see
// src/lib/offlineQueue.js). Anything else that fails goes back to its caller
// unchanged.
const QUEUEABLE_URL = /^\/stories\/\d+\/quiz$/;

client.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err.config || {};
    if (!err.response && cfg.method === "post" && QUEUEABLE_URL.test(cfg.url || "") && !cfg._skipQueue) {
      await enqueueWrite({ method: "post", url: cfg.url, data: cfg.data });
      err.queued = true;
    }
    throw err;
  },
);

// Used only by the offline queue's flush. Passes _skipQueue so a replay that
// fails for lack of a connection is not queued a second time.
export function sendQueuedWrite(item) {
  return client.request({ method: item.method, url: item.url, data: item.data, _skipQueue: true });
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default client;
